'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function YuzDolgusuSonrasiUcakSeyahati() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/yuz-dolgusu-sonrasi-ucak-seyahati-basinc-degisimi-odem-riski-ve-uzman-onerileri';
  const pageTitle = 'Yüz Dolgusu Sonrası Uçak Seyahati: Basınç Değişimi, Ödem Riski ve Uzman Önerileri';
  const pageDescription = 'Yüz dolgu işleminden hemen sonra uçağa binmek doğru mu? Basınç, dolaşım ve ödem üzerindeki etkiler ve bilimsel açıklamalar.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Yüz Dolgusu Sonrası Uçak Seyahati', href: '/tr/blog/yuz-dolgusu-sonrasi-ucak-seyahati-basinc-degisimi-odem-riski-ve-uzman-onerileri' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Yüz dolgusu sonrası uçak seyahati ve etkileri"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-sky-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Seyahat & Estetik
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Yüz Dolgusu Sonrası Uçak Seyahati: Basınç Değişimi, Ödem Riski ve Uzman Önerileri
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>5 Mart 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>8 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Yüz dolgu uygulamaları, kırışıklıkları azaltmak, hacim kaybını geri kazandırmak ve daha dinamik bir görünüm elde etmek için en sık başvurulan medikal estetik işlemlerdir.
                Ancak estetik sonucun doğal ve kalıcı olması sadece uygulamanın kalitesiyle değil, işlem sonrası gösterilen özenle de doğrudan ilişkilidir.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Hastaların sık sorduğu sorulardan biri şudur:
              </p>

              <div className="bg-sky-50 border-l-4 border-sky-400 p-4 mb-6">
                <p className="text-sky-800 font-medium text-lg">
                  "Dolgu yaptırdım, ertesi gün uçağa binebilir miyim?"
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                <p className="text-red-800 font-medium">
                  Kısa cevap: Hayır, ideal olan en az 48 saat beklemektir.
                </p>
                <p className="text-red-800">
                  Çünkü uçak içi basınç değişimleri ve dolaşım farkları, dolgunun dokudaki davranışını doğrudan etkileyebilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣ Uçak Basıncının Vücuda Etkisi
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Uçak kabini, deniz seviyesinden yaklaşık 2000 metre yüksekliğe denk gelen bir basınçta ayarlanır.
                Bu durumda vücuttaki hava ve sıvı hacimleri değişir:
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-blue-800">
                  <li>Damar içi basınç azalır.</li>
                  <li>Dokular arasında sıvı geçişi artar.</li>
                  <li>Hücre dışı sıvı hacmi (yani ödem) artabilir.</li>
                  <li>Yüzde geçici şişlik ve göz altı dolgunluğu oluşabilir.</li>
                </ul>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Bu etkiler sağlıklı kişide geçici olsa da, yeni dolgu yapılmış bir yüzde komplikasyon riskini yükseltir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2️⃣ Dolgu Sonrası Uçak Seyahatinde Olası Sorunlar
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-sky-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Durum</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Sebep</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Sonuç</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Basınç farkı</td>
                      <td className="border border-gray-300 px-4 py-3">Kabindeki basınç değişimi</td>
                      <td className="border border-gray-300 px-4 py-3">Dolgu içinde mikro hava kabarcıkları genleşebilir → asimetri görünümü</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Dolaşım bozukluğu</td>
                      <td className="border border-gray-300 px-4 py-3">Uzun süre hareketsizlik</td>
                      <td className="border border-gray-300 px-4 py-3">Lenf akışı yavaşlar, ödem artabilir</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Kabin kuruluğu</td>
                      <td className="border border-gray-300 px-4 py-3">Nem seviyesi %20'nin altında</td>
                      <td className="border border-gray-300 px-4 py-3">Hyaluronik asit su kaybeder, gerginlik hissi oluşur</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Basınç dalgalanması</td>
                      <td className="border border-gray-300 px-4 py-3">Kalkış ve iniş anlarında</td>
                      <td className="border border-gray-300 px-4 py-3">Yeni yerleşmiş dolgu maddesinde mikro hareket</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
                <p className="text-orange-800 font-medium">
                  📍 Sonuç: Uçuş sırasında dolgu henüz dokuyla tam bütünleşmeden mikro hareket ve ödem oluşabilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3️⃣ Hangi Bölgelerde Risk Yüksek?
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-purple-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Bölge</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Risk Düzeyi</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Göz altı</td>
                      <td className="border border-gray-300 px-4 py-3">Yüksek</td>
                      <td className="border border-gray-300 px-4 py-3">Basınç değişimine en duyarlı alan</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Elmacık kemiği</td>
                      <td className="border border-gray-300 px-4 py-3">Orta</td>
                      <td className="border border-gray-300 px-4 py-3">Basınç farkı ödem yapabilir</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Nazolabial bölge</td>
                      <td className="border border-gray-300 px-4 py-3">Orta</td>
                      <td className="border border-gray-300 px-4 py-3">Mimik kaslarının hareketsiz kalması dolgu yerleşimini etkiler</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Çene hattı / Jawline</td>
                      <td className="border border-gray-300 px-4 py-3">Düşük</td>
                      <td className="border border-gray-300 px-4 py-3">Genellikle daha stabil kalır</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4️⃣ Zamanlama Tablosu
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-green-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Zaman</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Durum</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Öneri</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">0–24 saat</td>
                      <td className="border border-gray-300 px-4 py-3">Damar geçirgenliği yüksek</td>
                      <td className="border border-gray-300 px-4 py-3">Uçuş kesinlikle önerilmez</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">24–48 saat</td>
                      <td className="border border-gray-300 px-4 py-3">Dolgu yerleşme dönemi</td>
                      <td className="border border-gray-300 px-4 py-3">Yüksek risk devam eder</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">48–72 saat</td>
                      <td className="border border-gray-300 px-4 py-3">Stabilizasyon başlar</td>
                      <td className="border border-gray-300 px-4 py-3">Kısa uçuş düşünülebilir</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">72 saat sonrası</td>
                      <td className="border border-gray-300 px-4 py-3">Dokular dengeye oturur</td>
                      <td className="border border-gray-300 px-4 py-3">Uçuş genellikle güvenlidir</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5️⃣ Bilimsel Açıklama: Basınç ve Hyaluronik Asit
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Hyaluronik asit hidrofilik (madde ile su bağlayan) bir moleküldür.
                Kabindeki düşük nem ve basınç değişimi, bu suyun kısmen serbest kalmasına neden olur.
                Bu da dolguda geçici hacim azalması ya da ödem görünümü oluşturabilir.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Ayrıca kabin kuruluğu cilt yüzeyinde gerginlik ve kuruluk hissi yaratır; bu da dolgu yapılan bölgede "sertlik varmış gibi" algılanabilir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6️⃣ Klinik Vaka Örneği
              </h2>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
                <p className="text-blue-800 italic">
                  "Bir hastam dolgu işleminden bir gün sonra 3 saatlik uçuşa katılmıştı.
                  Ertesi gün göz altında şişlik ve hafif asimetrik görünüm oluştu.
                  Bu durum tamamen basınç değişimine bağlıydı ve soğuk kompres ile 3 günde tamamen düzeldi."
                </p>
                <p className="text-blue-800 font-medium mt-4">
                  — Dr. Barış Çin, Estetik Cerrahi Uzmanı
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7️⃣ Uçuş Öncesi ve Sonrası Öneriler
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-yellow-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Durum</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Öneri</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Uçuş öncesi</td>
                      <td className="border border-gray-300 px-4 py-3">Bol su için, kafein ve alkolden kaçının</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Uçuş sırasında</td>
                      <td className="border border-gray-300 px-4 py-3">2 saatte bir hareket edin, su için</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Uçuş sonrası</td>
                      <td className="border border-gray-300 px-4 py-3">Yüzü soğuk su ile yıkayın, dolgu bölgesine masaj yapmayın</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 mb-6">
                <p className="text-indigo-800 font-medium">
                  💬 Kısacası: İlk 72 saatte uçmak, güzelliği ufak bir riskle teste çevirmektir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                8️⃣ Uzun Uçuşlarda Ek Riskler
              </h2>
              
              <div className="bg-red-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-red-800">
                  <li>Kabin içinde oksijen seviyesi azaldığı için dolaşım yavaşlar.</li>
                  <li>Özellikle göz altı ve yanak bölgelerinde ödem artışı görülür.</li>
                  <li>Vücut pozisyonu nedeniyle lenfatik akış kısıtlanır.</li>
                </ul>
                <p className="text-red-800 mt-4">
                  Bu nedenle dolgu işleminden hemen sonra uzun yol uçuşu planlamak doğru değildir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                9️⃣ Estetik ve Psikolojik Açıdan
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Dolgu sonrası hafif şişlik bile kişide "işlem fark ediliyor mu?" kaygısı yaratabilir.
                Uçak ortamı ise kuruluk, basınç ve yorgunluk nedeniyle yüzü daha gergin ve şiş gösterebilir.
              </p>

              <div className="bg-purple-50 border-l-4 border-purple-400 p-4 mb-6">
                <p className="text-purple-800 italic">
                  "İşlem sonrası güzellik, aceleyle değil, bakımla yükselir."
                </p>
                <p className="text-purple-800 font-medium mt-2">
                  — Dr. Barış Çin
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                🔟 Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Dolgu sonrası hemen uçağa binebilir miyim?</h3>
                  <p className="text-gray-600">Hayır. En az 48 saat beklenmelidir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Uçakta dolgu patlar mı?</h3>
                  <p className="text-gray-600">Hayır, ama basınç değişimi ödem ve hacim dalgalanmasına neden olabilir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Kısa iç hat uçuşu riskli midir?</h3>
                  <p className="text-gray-600">İlk 48 saat içindeyse evet.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Uçuş sonrası şişlik oldu, ne yapmalıyım?</h3>
                  <p className="text-gray-600">Soğuk kompres, bol su, yüksek yastıkta uyuma önerilir.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5. Uzun uçuş ne kadar riskli?</h3>
                  <p className="text-gray-600">İlk 72 saatte uzun uçuş önerilmez, ödem riski artar.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">6. Uçuş sırasında ne yapmalıyım?</h3>
                  <p className="text-gray-600">Bol su için, 2 saatte bir hareket edin, yüz masajı yapmayın.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sonuç ve Klinik Değerlendirme
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Yüz dolgu sonrası uçak seyahati, görünürde basit bir detay gibi dursa da estetik sonucun kalitesini belirleyen önemli bir faktördür.
                İlk 48–72 saatte uçuş, ödem ve dolgu asimetrisi riskini artırabilir.
              </p>

              <div className="bg-red-50 border-l-4 border-red-400 p-6 mb-6">
                <p className="text-red-800 font-medium">
                  🩺 Dr. Barış Çin öneriyor:
                </p>
                <p className="text-red-800 mt-2">
                  "Dolgu işleminden sonra vücudun denge kurmasına izin verin. Uçak bir yere gitmez, ama dolgu sabit kalmaz."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>ISAPS Global Report (2024) – "Dermal Filler Complications and Flight Pressure"</li>
                  <li>Teoxane Clinical Studies (2023) – "HA Behavior Under Atmospheric Pressure"</li>
                  <li>Harvard Health (2024) – "Air Travel and Microcirculation Dynamics"</li>
                  <li>Türk Estetik Tıp Derneği (TETD) – "Dolgu Sonrası Seyahat Rehberi"</li>
                </ul>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-sky-100 text-sky-800 px-3 py-1 rounded-full text-sm">#yüzdolgu</span>
                <span className="bg-sky-100 text-sky-800 px-3 py-1 rounded-full text-sm">#uçakseyahati</span>
                <span className="bg-sky-100 text-sky-800 px-3 py-1 rounded-full text-sm">#ödem</span>
                <span className="bg-sky-100 text-sky-800 px-3 py-1 rounded-full text-sm">#estetik</span>
                <span className="bg-sky-100 text-sky-800 px-3 py-1 rounded-full text-sm">#DrBarışÇin</span>
                <span className="bg-sky-100 text-sky-800 px-3 py-1 rounded-full text-sm">#dolgu</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-sky-600 to-blue-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Yüz Dolgusu Uygulaması ve Seyahat Danışmanlığı</h3>
          <p className="mb-6">Uzman doktorumuzdan yüz dolgusu uygulaması ve seyahat planlaması hakkında detaylı bilgi alın.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Yüz dolgusu uygulaması ve seyahat planlaması hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-sky-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/yuz-dolgusu-sonrasi-alkol-aliminin-sonuclari"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Yüz Dolgusu Sonrası Alkol Alımının Sonuçları</h4>
              <p className="text-gray-600 text-sm">Yüz dolgu uygulamasından sonra alkol içmek neden sakıncalıdır? Morarma, ödem ve dolgu yer değişimi.</p>
            </a>
            <a
              href="/tr/blog/dudak-dolgusu-sonrasi-alkol-tuketimi-bilimsel-etkiler-klinik-bulgular-ve-estetik-sonuclar"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Dudak Dolgusu Sonrası Alkol Tüketimi</h4>
              <p className="text-gray-600 text-sm">Dudak dolgusu sonrası alkol neden önerilmez? Hyaluronik asit dengesi ve damar geçirgenliği.</p>
            </a>
            <a
              href="/tr/blog/sivi-yuz-germe-nedir-hangi-icerikli-urunler-kullanilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Sıvı Yüz Germe Nedir?</h4>
              <p className="text-gray-600 text-sm">Sıvı yüz germe nedir, hangi içerikli ürünler kullanılır? Hacim artırıcı dolgular ve uygulama teknikleri.</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-sky-600 text-white rounded-full hover:bg-sky-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
